// React
import { memo, useCallback, useEffect, useMemo, useState } from "react";

// Helpers
import { classNames, useTranslate } from "helpers";

// Servisni sloj
import {
  approveInvoiceIncomingAPI,
  buildInvoiceIncomingDocumentQueueAPI,
  createInvoiceIncomingAPI,
  extractInvoiceIncomingDocumentAPI,
  getInvoiceIncomingAllocationsAPI,
  getInvoiceIncomingAPI,
  updateInvoiceIncomingAPI,
} from "services/api/finance/incoming-invoices/api";
import { initialInvoiceIncomingEditForm } from "services/api/finance/incoming-invoices/consts";
import { transformInvoiceIncomingEditForm } from "services/api/finance/incoming-invoices/transformations";
import {
  InvoiceIncoming,
  InvoiceIncomingCostAllocation,
  InvoiceIncomingEditFormValues,
  InvoiceIncomingExtraction,
  InvoiceIncomingItemRow,
  InvoiceIncomingQueueItem,
} from "services/api/finance/incoming-invoices/types";
import { calculateBillingLineNet, calculateBillingLineTotal } from "services/api/finance/billing/calculations";
import { showNotification } from "services/api/snackbar/index";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { useHandleTextFieldChange } from "services/forms/hooks";

// UI
import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";
import ModalHeader from "ui/components/Modal/ModalHeader";
import Tabs from "ui/components/Tabs/Tabs";
import { TextArea } from "ui/components/TextField/TextArea";

// Lokalne komponente
import InvoiceIncomingEditInfo from "./components/InvoiceIncomingEditInfo/InvoiceIncomingEditInfo";
import InvoiceDocumentViewer from "./components/InvoiceDocumentViewer/InvoiceDocumentViewer";
import { buildInvoiceFieldValues } from "./components/InvoiceDocumentViewer/transformations";
import InvoiceIncomingFileIntake from "./components/InvoiceIncomingFileIntake/InvoiceIncomingFileIntake";
import InvoiceIncomingEditItems from "./components/InvoiceIncomingEditItems/InvoiceIncomingEditItems";
import InvoiceIncomingAllocationModal from "ui/modules/finance/pages/invoices/InvoiceSearchPage/components/InvoiceIncomingPreviewCard/components/InvoiceIncomingAllocationModal/InvoiceIncomingAllocationModal";
import { INVOICE_INCOMING_EDIT_FORM_SCHEMA } from "./validation";
import { buildFormValuesFromExtraction } from "./consts";
import InvoiceIncomingFileDrop from "./components/InvoiceIncomingFileDrop/InvoiceIncomingFileDrop";
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";
import { InvoiceIncomingEditCardProps } from "./types";

/**
 * Unos ulaznog računa koji ne dolazi kroz providera. Isti obrazac služi za ručni unos i za račune
 * učitane iz datoteke: ubačene datoteke idu u red, obrađuju se jedna po jedna, a korisnik svaku
 * potvrdi prije spremanja. E-računi se ovdje ne uređuju; njih poslužitelj brani od prepisivanja.
 */
const InvoiceIncomingEditCard: React.FC<InvoiceIncomingEditCardProps> = (props) => {
  const { show, invoiceId, vehicleId, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<InvoiceIncomingEditFormValues>(initialInvoiceIncomingEditForm);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Red datoteka koje čekaju unos; prazan red znači obični ručni unos.
  const [queue, setQueue] = useState<InvoiceIncomingQueueItem[]>([]);
  const [queueIndex, setQueueIndex] = useState<number>(0);
  const [rejectedFiles, setRejectedFiles] = useState<{ fileName: string; reason: string }[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busyText, setBusyText] = useState<string>("");
  const [savedCount, setSavedCount] = useState<number>(0);

  // Odabir datoteka ide kroz zajednički modal, koji se kod novog računa otvara odmah.
  const [showFileModal, setShowFileModal] = useState<boolean>(false);

  // Raspored troškova postojećeg računa; prazan znači da se raspodjela tek nudi.
  const [allocations, setAllocations] = useState<InvoiceIncomingCostAllocation[]>([]);
  const [showAllocationModal, setShowAllocationModal] = useState<boolean>(false);

  // Tek spremljeni račun čeka raspodjelu; dok čeka, obrazac ne prelazi na sljedeću datoteku.
  const [pendingAllocation, setPendingAllocation] = useState<InvoiceIncoming | null>(null);

  // Polje na kojem je korisnik — po njemu se ističe mjesto na dokumentu.
  const [activeField, setActiveField] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<InvoiceIncomingExtraction | null>(null);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);

  useEffect(() => {
    if (!show) return;

    setErrors({});

    if (invoiceId) {
      fetchInvoice(invoiceId);
      return;
    }

    resetForm();
    setQueue([]);
    setQueueIndex(0);
    setRejectedFiles([]);
    setWarnings([]);
    setSavedCount(0);
    setExtraction(null);
    setActiveField(null);

    // Novi račun najčešće dolazi iz datoteke, pa odabir kreće odmah; tko unosi ručno modal zatvori.
    setShowFileModal(true);
  }, [show, invoiceId, vehicleId]);

  // Dokle se stiglo kroz red javlja obavijest u kutu — na ekranu je već vidljiv sam dokument, pa bi
  // ista informacija nad obrascem samo trošila prostor.
  useEffect(() => {
    const item = queue[queueIndex];
    if (!show || !item) return;

    showNotification(`Račun ${queueIndex + 1} od ${queue.length} — ${item.fileName}`, {
      variant: "info",
    });
  }, [show, queue, queueIndex]);

  const resetForm = useCallback(() => {
    setFormValues({
      ...initialInvoiceIncomingEditForm,
      invoiceDate: new Date(),
      statusId: "received",
      incomingSourceId: "manual",
      vehicleId: vehicleId ?? null,
      items: [],
    });
  }, [vehicleId]);

  const fetchInvoice = async (id: string) => {
    setIsLoading(true);

    try {
      const result = await getInvoiceIncomingAPI(id);
      setFormValues(transformInvoiceIncomingEditForm(result));
    } catch {
      // greška se prikazuje kroz snackbar
    }

    await fetchAllocations(id);

    setIsLoading(false);
  };

  /** Raspored troškova — po njemu se zna nudi li se raspodjela ili je već napravljena. */
  const fetchAllocations = async (id: string) => {
    try {
      setAllocations(await getInvoiceIncomingAllocationsAPI(id));
    } catch {
      setAllocations([]);
    }
  };

  /**
   * Čita račun s datoteke i popunjava obrazac. Podaci se ne spremaju — korisnik ih potvrđuje.
   */
  const loadExtraction = useCallback(async (item: InvoiceIncomingQueueItem) => {
    setIsLoading(true);
    setBusyText("Čitam račun…");
    setErrors({});

    let extraction: InvoiceIncomingExtraction | null = null;

    try {
      extraction = await extractInvoiceIncomingDocumentAPI(item.uploadId);
    } catch {
      // greška se prikazuje kroz snackbar; račun se unosi ručno
    }

    setExtraction(extraction);

    if (extraction) {
      setFormValues(buildFormValuesFromExtraction(extraction, item.uploadId, vehicleId ?? null));
      setWarnings(extraction.warnings);
    } else {
      resetForm();
      setFormValues(prev => ({ ...prev, uploadIds: [item.uploadId], incomingSourceId: "upload" }));
      setWarnings([]);

      // Neuspjelo čitanje je obavijest, a ne nešto što treba stajati nad obrascem — upozorenja
      // iznad obrasca odnose se na pročitane podatke koje korisnik mora provjeriti.
      showNotification("Podatke s računa nije bilo moguće pročitati — unesite ih ručno.", {
        variant: "warning",
      });
    }

    setBusyText("");
    setIsLoading(false);
  }, [resetForm, vehicleId]);

  const handleFilesUploaded = useCallback(async (uploads: FleetDocumentUpload[]) => {
    if (uploads.length === 0) return;

    setIsLoading(true);
    setBusyText("Pripremam datoteke…");

    try {
      const result = await buildInvoiceIncomingDocumentQueueAPI(uploads.map(upload => upload.id));

      setRejectedFiles(result.rejected);

      if (result.queue.length === 0) {
        setBusyText("");
        setIsLoading(false);
        return;
      }

      setShowFileModal(false);
      setQueue(result.queue);
      setQueueIndex(0);
      await loadExtraction(result.queue[0]);
    } catch {
      setBusyText("");
      setIsLoading(false);
    }
  }, [loadExtraction]);

  /** Ponovno čita tekuću datoteku — koristi se kad je rezultat loš ili su postavke promijenjene. */
  const handleReExtract = useCallback(() => {
    const item = queue[queueIndex];
    if (item) loadExtraction(item);
  }, [queue, queueIndex, loadExtraction]);

  /** Prelazi na sljedeću datoteku iz reda; kad ih nestane, obrazac se zatvara. */
  const goToNext = useCallback(async () => {
    const nextIndex = queueIndex + 1;

    if (nextIndex >= queue.length) {
      onSuccess();
      onClose();
      return;
    }

    setQueueIndex(nextIndex);
    setWarnings([]);
    await loadExtraction(queue[nextIndex]);
  }, [queue, queueIndex, loadExtraction, onSuccess, onClose]);

  const handleSkip = useCallback(() => {
    goToNext();
  }, [goToNext]);

  // Poslužitelj preuzima iznose iz zahtjeva, pa ih računamo iz stavki prije slanja.
  const totals = useMemo(() => {
    const activeItems = formValues.items.filter(item => !item.deleted);

    const totalWithoutTax = activeItems.reduce((sum, item) => sum + calculateBillingLineNet(item), 0);
    const total = activeItems.reduce((sum, item) => sum + calculateBillingLineTotal(item), 0);

    return {
      totalWithoutTax: Number(totalWithoutTax.toFixed(2)),
      totalTax: Number((total - totalWithoutTax).toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  }, [formValues.items]);

  /** Nakon spremanja: kod unosa iz datoteka ide sljedeća, inače se pretraga osvježi. */
  const continueAfterSave = useCallback(async () => {
    if (queue.length > 0) {
      setSavedCount(prev => prev + 1);
      await goToNext();
      return;
    }

    onSuccess();
  }, [queue.length, goToNext, onSuccess]);

  /**
   * Sprema račun i, kad se traži, odmah ga odobrava. Odobrenje je zasebna radnja jer se na njemu
   * vidi tko je račun prihvatio, a tek iz odobrenog računa nastaje trošak.
   */
  const saveInvoice = useCallback(async (approve: boolean) => {
    const isValid = validateYup(formValues, INVOICE_INCOMING_EDIT_FORM_SCHEMA, setErrors);
    if (!isValid) return;

    const payload: InvoiceIncomingEditFormValues = {
      ...formValues,
      incomingSourceId: formValues.incomingSourceId ?? "manual",
      statusId: formValues.statusId ?? "received",
      totalWithoutTax: totals.totalWithoutTax,
      totalTax: totals.totalTax,
      total: totals.total,
    };

    setIsSaving(true);

    try {
      const saved = payload.id
        ? await updateInvoiceIncomingAPI(payload.id, payload, t)
        : await createInvoiceIncomingAPI(payload, t);

      if (approve && saved?.id) {
        await approveInvoiceIncomingAPI(saved.id, t);
      }

      const savedItems = saved?.items?.filter(item => !item.deleted) ?? [];

      // Trošak nastaje iz stavki, pa se raspodjela nudi odmah nakon spremanja — tada je račun
      // još pred korisnikom. Prelazak na sljedeću datoteku čeka da modal ode.
      if (saved?.id && savedItems.length > 0 && allocations.length === 0) {
        setPendingAllocation(saved);
        setShowAllocationModal(true);
        setIsSaving(false);
        return;
      }

      await continueAfterSave();
    } catch {
      // greška se prikazuje kroz snackbar
    }

    setIsSaving(false);
  }, [formValues, t, totals, allocations.length, continueAfterSave]);

  const handleSave = useCallback(() => {
    saveInvoice(false);
  }, [saveInvoice]);

  const handleSaveAndApprove = useCallback(() => {
    saveInvoice(true);
  }, [saveInvoice]);

  const title = formValues.invoiceNumber
    ? `Ulazni račun ${formValues.invoiceNumber}`
    : "Kreiranje ulaznog računa";

  const currentQueueItem = queue[queueIndex];
  const isFromQueue = queue.length > 0;
  const hasMoreInQueue = queueIndex + 1 < queue.length;
  const hasIntakeInfo = warnings.length > 0 || rejectedFiles.length > 0;

  const handleAllocationModalOpen = useCallback(() => {
    setShowAllocationModal(true);
  }, []);

  /** Raspodjela nakon spremanja nije obavezna — zatvaranje modala samo nastavlja tok. */
  const handleAllocationModalHide = useCallback(() => {
    setShowAllocationModal(false);

    if (!pendingAllocation) return;

    setPendingAllocation(null);
    continueAfterSave();
  }, [pendingAllocation, continueAfterSave]);

  const handleAllocationModalSuccess = useCallback(() => {
    setShowAllocationModal(false);

    if (pendingAllocation) {
      setPendingAllocation(null);
      continueAfterSave();
      return;
    }

    if (formValues.id) fetchAllocations(formValues.id);
  }, [pendingAllocation, continueAfterSave, formValues.id]);

  // Dokument zamjenjuje odabir datoteka čim je red spreman.
  const showDocument = !!currentQueueItem && !showFileModal;

  // Vrijednosti iz obrasca po ključevima s dokumenta — prikazuju se na hover nad označenim mjestom.
  const fieldValues = useMemo(() => buildInvoiceFieldValues(formValues), [formValues]);

  // Raspodjela traži spremljen račun sa stavkama — troškovi se vežu na njihove id-eve. Status se
  // ne gleda: poslužitelj raspodjelu dopušta i prije odobrenja, a nudi se dok je nema, jer se
  // postojeći raspored mijenja na kartici računa.
  const activeItems = formValues.items.filter(item => !item.deleted);
  const canAllocate = !!formValues.id && activeItems.length > 0 && allocations.length === 0;

  // Odobrenje se nudi samo dok račun čeka odluku; odobren, odbijen ili plaćen se ovdje ne dira.
  const canApproveOnSave = !formValues.statusId || formValues.statusId === "received";

  // Raspodjela ide ili nad tek spremljenim računom, ili nad onim koji se uređuje.
  const allocationInvoiceId = pendingAllocation?.id ?? formValues.id;

  return (
    // Puna širina jer dokument i obrazac stoje jedan uz drugoga u istom modalu, ispod zajedničkog
    // zaglavlja — isti raspored kao kod fotografija štete.
    <Modal show={show} onHide={onClose} position="side" size="full">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={title}
        onHide={onClose}
      />
      <ModalBody overflow={false} fixedFooterSpacing={false}>
        {/* Na uskom ekranu dokument i obrazac idu jedno ispod drugoga — jedan uz drugi bi oba
            ispala preuska za rad. */}
        <div className="flex flex-col lg:flex-row h-full min-h-0">
          {/* Lijeva polovica: dokument koji se upravo unosi, a dok ga nema — odabir datoteka. */}
          {!invoiceId && (
            <div className="w-full lg:w-1/2 h-[45vh] lg:h-auto shrink-0 lg:shrink min-h-0 border-b lg:border-b-0 lg:border-r border-slate-200">
              {showDocument ? (
                <InvoiceDocumentViewer
                  url={currentQueueItem?.url ?? null}
                  fileExtension={currentQueueItem?.fileExtension ?? null}
                  fieldLocations={extraction?.fieldLocations}
                  activeField={activeField}
                  rotation={extraction?.detectedRotation}
                  fieldValues={fieldValues}
                />
              ) : (
                <InvoiceIncomingFileDrop
                  onUploaded={handleFilesUploaded}
                  isBusy={isLoading}
                  busyText={busyText}
                />
              )}
            </div>
          )}

          <div className={classNames("min-h-0 overflow-y-auto", invoiceId ? "w-full" : "w-full lg:w-1/2")}>
            {/* Upozorenja o pročitanim podacima stoje nad obrascem; kod uređivanja ih nema, a kod
                ručnog unosa nema što upozoravati, pa otpadaju zajedno s praznim razmakom. */}
            {!invoiceId && hasIntakeInfo && (
              <div className="px-8 pt-6">
                <InvoiceIncomingFileIntake rejected={rejectedFiles} warnings={warnings} />
              </div>
            )}

            {isLoading && (
              <div className="relative h-[calc(100vh-220px)]">
                <Loader isLoading={true}>{null}</Loader>
              </div>
            )}

            <form noValidate onSubmit={(e) => e.preventDefault()} className={isLoading ? "hidden" : ""}>
              <div className="flex mt-6 mb-6 px-4 min-h-[60vh] xl:min-h-[800px]">
                <Tabs
                  tabs={[
                    {
                      label: "Osnovne informacije",
                      content: (
                        <div className="px-4 pt-3 pb-4 border border-slate-200 -mt-[1px]">
                          <InvoiceIncomingEditInfo
                            formValues={formValues}
                            setFormValues={setFormValues}
                            errors={errors}
                            fieldConfidences={extraction?.fieldConfidences}
                            onFieldFocus={setActiveField}
                          />

                          {/* Stavke stoje odmah ispod zaglavlja, kao na izlaznim računima — račun se
                              provjerava u cjelini, pa ih nema smisla skrivati pod zaseban tab. */}
                          <div className="mt-6">
                            <div className="px-4 py-2 text-sm font-medium border-b-2 border-sky-600 text-sky-600">
                              Stavke računa
                            </div>
                            <div className="border-x border-b border-slate-300 rounded-b-md">
                              <InvoiceIncomingEditItems
                                formValues={formValues}
                                setFormValues={setFormValues}
                              />
                            </div>
                          </div>

                          {/* Napomena ide ispod stavki, kao na izlaznim računima — račun se čita
                              odozgo: zaglavlje, pa stavke, pa napomena. */}
                          <div className="mt-6">
                            <TextArea
                              name="note"
                              label="Napomena"
                              value={formValues.note}
                              onChange={handleTextFieldChange}
                              rows={3}
                            />
                          </div>
                        </div>
                      ),
                    },
                    {
                      label: "Interna napomena",
                      content: (
                        <div className="px-4 py-4 border border-slate-200 -mt-[1px]">
                          <TextArea
                            name="internalNote"
                            label="Interna napomena"
                            placeholder="Vidljiva samo unutar tvrtke"
                            value={formValues.internalNote}
                            onChange={handleTextFieldChange}
                            rows={6}
                          />
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </form>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className="px-4 py-3 flex justify-between items-center gap-3">
          <Button variant="outlined" children="Zatvori" onClick={onClose} />

          <div className="flex items-center gap-3">
            {savedCount > 0 && (
              <span className="text-sm text-slate-500">Spremljeno: {savedCount}</span>
            )}

            {/* Račun bez rasporeda troškova nema od čega nastati trošak, pa gumb zove na akciju. */}
            {canAllocate && (
              <Button
                variant="warning"
                children="Raspodjeli troškove"
                onClick={handleAllocationModalOpen}
                disabled={isLoading || isSaving}
              />
            )}

            {/* Radnje nad tekućim računom stoje uz spremanje — sve su izbor nad istim računom. */}
            {!invoiceId && isFromQueue && (
              <Button
                variant="outlined"
                children="Pročitaj ponovno"
                onClick={handleReExtract}
                disabled={isLoading || isSaving}
              />
            )}

            {/* Preskakanje se nudi samo kad iza računa još nešto čeka — kod jedne datoteke
                jednako je zatvaranju. */}
            {!invoiceId && hasMoreInQueue && (
              <Button
                variant="outlined"
                children="Preskoči"
                onClick={handleSkip}
                disabled={isLoading || isSaving}
              />
            )}

            <Button
              variant={canApproveOnSave ? "outlined" : "primary"}
              children={
                isFromQueue
                  ? (hasMoreInQueue ? "Spremi i sljedeći" : "Spremi i zatvori")
                  : formValues.id ? "Spremi" : "Kreiraj račun"
              }
              onClick={handleSave}
              disabled={isSaving || isLoading}
            />

            {/* Račun koji netko iz tvrtke unosi ručno time je i prihvaćen, pa mu odobrenje ide
                odmah — tek iz odobrenog računa nastaje trošak. */}
            {canApproveOnSave && (
              <Button
                variant="primary"
                children="Spremi i odobri"
                onClick={handleSaveAndApprove}
                disabled={isSaving || isLoading}
              />
            )}
          </div>
        </div>
      </ModalFooter>

      {allocationInvoiceId && (
        <InvoiceIncomingAllocationModal
          show={showAllocationModal}
          invoiceId={allocationInvoiceId}
          invoiceNumber={pendingAllocation?.invoiceNumber ?? formValues.invoiceNumber}
          items={pendingAllocation?.items ?? (activeItems as InvoiceIncomingItemRow[])}
          defaultVehicleId={formValues.vehicleId}
          onHide={handleAllocationModalHide}
          onSuccess={handleAllocationModalSuccess}
        />
      )}
    </Modal>
  );
};

export default memo(InvoiceIncomingEditCard);
